import { useMemo } from "react"
import { PaginationState } from "@tanstack/react-table"
import {
  useFetchIntegrationsQuery,
  useGetIntegrationDetailsQuery,
  useGetStatusQuery,
  useFetchBrandsQuery
} from "../../redux/queries"

interface UseIntegrationsPageDataOpts {
  id?: string
  isSuperAdmin: boolean
  orgId?: string
  pagination?: PaginationState
  /** `""` = no API filter (all brands) */
  selectedBrandId?: string
}

export function useIntegrationsPageData({
  id,
  isSuperAdmin,
  orgId,
  pagination,
  selectedBrandId
}: UseIntegrationsPageDataOpts) {
  const orgFilter = isSuperAdmin ? {} : { org_id: orgId }

  const queryParams = useMemo(() => {
    const brand_id = selectedBrandId || undefined
    return {
      limit: pagination?.pageSize ?? 10,
      offset: pagination ? pagination.pageIndex * pagination.pageSize : 0,
      ...orgFilter,
      ...(brand_id ? { brand_id } : {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, orgId, pagination, selectedBrandId])

  const brandsQueryParams = useMemo(
    () => ({ limit: 999, ...orgFilter }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin, orgId]
  )

  const { data: brands } = useFetchBrandsQuery(brandsQueryParams)
  const {
    data: integrationsResponse,
    isLoading: isLoadingIntegrations,
    refetch
  } = useFetchIntegrationsQuery(queryParams, {
    refetchOnMountOrArgChange: true
  })

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(id ?? "", {
      skip: !id
    })

  const { data: statusData = [], isLoading: isLoadingStatus } =
    useGetStatusQuery(
      { integrationID: integration?.id ?? "" },
      { skip: !integration?.id }
    )

  return {
    brands,
    integrations: integrationsResponse?.results,
    integrationsTotal: integrationsResponse?.total,
    integration,
    statusData,
    isLoadingIntegrations,
    isLoadingIntegration,
    isLoadingStatus
  }
}
